import { Link, router } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import { common, pick } from "../../lib/i18n";
import type { CurrentUser, ModelPersonaDetail, ProductSummary } from "../../types";

const copy = {
  pl: {
    confirmDelete: (n: string) => `Usunąć personę „${n}"?`, inactiveNote: "Persona nieaktywna - nie pojawia się w formularzu generacji",
    details: "Szczegóły", status: "Status", active: "Aktywna", inactive: "Nieaktywna",
    skinTone: "Odcień skóry", ageBand: "Przedział wiekowy", presentation: "Prezentacja",
    handsStyling: "Stylizacja dłoni", hairStyling: "Stylizacja włosów", bodyFocus: "Kadr / focus",
    appearanceNotesUpper: "DODATKOWE CECHY",
  },
  en: {
    confirmDelete: (n: string) => `Delete persona "${n}"?`, inactiveNote: "Persona inactive - it does not appear in the generation form",
    details: "Details", status: "Status", active: "Active", inactive: "Inactive",
    skinTone: "Skin tone", ageBand: "Age band", presentation: "Presentation",
    handsStyling: "Hands styling", hairStyling: "Hair styling", bodyFocus: "Framing / focus",
    appearanceNotesUpper: "APPEARANCE NOTES",
  },
};

type Props = {
  persona: ModelPersonaDetail;
  current_user: CurrentUser;
  products?: ProductSummary[];
};

export default function PersonasShow({ persona, current_user, products = [] }: Props) {
  const initials = current_user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const canEdit  = current_user.role === "admin" || current_user.role === "moderator";
  const t = pick(copy);
  const c = common();

  const handleDestroy = () => {
    if (confirm(t.confirmDelete(persona.name))) {
      router.delete(`/personas/${persona.id}`);
    }
  };

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page" style={{ maxWidth: 720 }}>
        <div className="page-hd">
          <div>
            <h2>{persona.name}</h2>
            {!persona.active && (
              <p style={{ color: "var(--muted)" }}>{t.inactiveNote}</p>
            )}
          </div>
          {canEdit && (
            <div className="right">
              <Link href={`/personas/${persona.id}/edit`} className="btn btn-secondary btn-sm">{c.actions.edit}</Link>
              <button className="btn btn-ghost btn-sm" onClick={handleDestroy}>{c.actions.delete}</button>
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel-hd"><h3>{t.details}</h3></div>
          <div className="panel-body">
            <div className="field-row">
              <span>{t.status}</span>
              <span>{persona.active ? t.active : t.inactive}</span>
            </div>
            <div className="field-row">
              <span>{t.skinTone}</span>
              <span>{persona.skin_tone}/10</span>
            </div>
            <div className="field-row">
              <span>{t.ageBand}</span>
              <span>{persona.age_band.replace("_", "-")}</span>
            </div>
            <div className="field-row">
              <span>{t.presentation}</span>
              <span>{persona.presentation}</span>
            </div>
            <div className="field-row">
              <span>{t.handsStyling}</span>
              <span>{persona.hands_styling.replace("_", " ")}</span>
            </div>
            <div className="field-row">
              <span>{t.hairStyling}</span>
              <span>{persona.hair_styling.replace("_", " ")}</span>
            </div>
            <div className="field-row">
              <span>{t.bodyFocus}</span>
              <span>{persona.body_focus.replace("_", " ")}</span>
            </div>
            {persona.appearance_notes && (
              <div style={{ marginTop: 10 }}>
                <div className="mono" style={{ fontSize: 10.5, color: "var(--muted-2)", marginBottom: 4 }}>{t.appearanceNotesUpper}</div>
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{persona.appearance_notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
